<template>
  <button
    v-show="!hidden"
    :class="getClass()"
    :type="getType()"
    :disabled="disabled"
    :form="form"
    @click="onClick"
  >
    <img v-if="iconSrc && iconPosition === 'left'" :src="iconSrc" alt="icon" />
    <i v-else-if="icon && iconPosition === 'left'" :class="getIconClass()"></i>

    <span
      v-if="label"
      :style="{
        marginLeft: icon && label && iconPosition === 'left' ? '4px' : 0,
        marginRight: icon && label && iconPosition === 'right' ? '4px' : 0
      }"
    >
      {{ label }}
    </span>

    <img v-if="iconSrc && iconPosition === 'right'" :src="iconSrc" alt="icon" />
    <i v-else-if="icon && iconPosition === 'right'" :class="getIconClass()"></i>
  </button>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

export const ButtonTypes = {
  size: VueTypes.oneOf([
    "sm",
    "md",
    "lg",
    "regular",
    "wide",
    "narrow",
    "giant",
    "fit"
  ]).def("md"),
  type: VueTypes.oneOf([
    "primary",
    "secondary",
    "tertiary",
    "danger",
    "dark",
    "success",
    "inconspicuous"
  ]).def("primary"),
  // to display with white background
  inverted: VueTypes.bool.def(false),
  noBorder: VueTypes.bool.def(false),
  // material icon name
  // e.g. "plus"
  icon: VueTypes.string,
  submit: VueTypes.bool.def(false),
  disabled: VueTypes.bool.def(false),
  hidden: VueTypes.bool.def(false),
  label: VueTypes.string,

  // icon SRC for custom icons (not material)
  iconSrc: VueTypes.string,

  // for dynamic click trigger
  onClickHandler: VueTypes.func,
  form: VueTypes.string,
  wide: VueTypes.bool.def(false),

  extraClass: VueTypes.string,
  iconPosition: VueTypes.oneOf(["left", "right"]).def("left")
};

export default Vue.component("VButton", {
  props: ButtonTypes,

  methods: {
    onClick(e) {
      if (this.onClickHandler) {
        this.onClickHandler(e);
      }

      this.$emit("click", e);
    },

    onLinkClick(e) {
      e.preventDefault();

      this.onClick(e);
    },

    getClass() {
      let base = `${this.type} ${this.size} ${
        this.iconSrc ? "with-image" : ""
      }`;

      if (this.inverted) {
        base += " inverted";
      }

      if (this.noBorder) {
        base += " noBorder";
      }

      if (this.link) {
        base += " link inverted";
      }

      if (this.wide) {
        base += " wide";
      }

      if (this.extraClass) {
        base += ` ${this.extraClass}`;
      }

      return base;
    },

    getType() {
      return this.submit ? "submit" : "button";
    },

    getIconClass() {
      return `fa fa-${this.icon} ${this.iconPosition}`;
    }
  }
});
</script>

<style lang="scss" scoped>
@mixin buttonColorSet($color) {
  &,
  &:hover {
    background: $color;
    border: 1px solid $color;
  }

  &.inverted {
    color: $color;
    border-color: $color;

    &:hover {
      background: rgba($color, 0.2);
      color: $color;
    }
  }
}

button {
  display: inline-block;
  vertical-align: middle;
  min-width: 104px;
  font-size: 14px;
  text-align: center;
  border: none;
  border-radius: 4px;
  color: $white;
  cursor: pointer;
  font-family: "Lato-Regular", Arial, sans-serif;
  transition: background 0.2s linear;
  height: $input-height;

  &.with-image {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    img {
      width: 22px;
      height: 22px;
    }
  }

  &.sm {
    font-size: 13px;
    padding: 0 15px;

    i {
      font-size: 10px;
    }
  }

  &.wide {
    min-width: 137px;
  }

  &.lg {
    font-size: 18px;
    padding: 18px 54px;
  }

  &.regular {
    font-size: 15px;
    width: 180px;
  }

  &.narrow {
    font-size: 15px;
    min-width: 70px;
  }
  &.giant {
    font-size: 16px;
    min-width: 300px;
    height: 60px;
  }

  &.fit {
    font-size: 16px;
    min-width: fit-content;
    height: fit-content;
  }

  &:focus {
    outline: none;
  }

  &.primary {
    @include buttonColorSet($button-primary-color);

    &:hover {
      background: #3f3ff2;
    }

    &:disabled {
      &,
      &:hover {
        background: transparent;
        color: $disabled-color;
        border: 1px solid $disabled-color;
        cursor: not-allowed;
      }
    }
  }

  &.secondary {
    font-size: 15px;
    border: none;
    background: transparent;
    color: lighten($primary-color, 10%);

    &:hover {
      color: $primary-color;
    }
  }

  &.danger {
    @include buttonColorSet($button-danger-color);
  }

  &.tertiary {
    @include buttonColorSet($tertiary-color);
  }

  &.dark {
    @include buttonColorSet($button-dark-color);
  }

  &.gray {
    @include buttonColorSet($secondary-border-color);
  }

  &.success {
    @include buttonColorSet($success-color);
  }

  &.inverted {
    background: $white;
    border: 1px solid;

    &:hover {
      color: $white;
    }
  }

  &.noBorder {
    border: none;
    &:hover {
      border: none;
    }
  }

  &:disabled {
    &,
    &:hover {
      background: transparent;
      color: $disabled-color;
      border: 1px solid $disabled-color;
      cursor: not-allowed;
    }
  }
}
</style>
